import _slicedToArray from "/home/vsts/work/1/s/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { jsxs, jsx } from 'react/jsx-runtime';
import { ErrorMessage } from '@promo-platform/error-message';
import { FieldHead } from '@promo-platform/field-head';
import { useConditionsWatch } from '@promo-platform/utils';
import classNames from 'classnames';
import { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useFormContext, Controller } from 'react-hook-form';
function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}
function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || _typeof(argument) === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }
    return new Date(NaN);
  }
}

/**
 * @name compareAsc
 * @category Common Helpers
 * @summary Compare the two dates and return -1, 0 or 1.
 *
 * @description
 * Compare the two dates and return 1 if the first date is after the second,
 * -1 if the first date is before the second or 0 if dates are equal.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to compare
 * @param {Date|Number} dateRight - the second date to compare
 * @returns {Number} the result of the comparison
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Compare 11 February 1987 and 10 July 1989:
 * const result = compareAsc(new Date(1987, 1, 11), new Date(1989, 6, 10))
 * //=> -1
 *
 * @example
 * // Sort the array of dates:
 * const result = [
 *   new Date(1995, 6, 2),
 *   new Date(1987, 1, 11),
 *   new Date(1989, 6, 10)
 * ].sort(compareAsc)
 * //=> [
 * //   Wed Feb 11 1987 00:00:00,
 * //   Mon Jul 10 1989 00:00:00,
 * //   Sun Jul 02 1995 00:00:00
 * // ]
 */

function compareAsc(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var diff = dateLeft.getTime() - dateRight.getTime();
  if (diff < 0) {
    return -1;
  } else if (diff > 0) {
    return 1; // Return 0 if diff is 0; return NaN if diff is NaN
  } else {
    return diff;
  }
}

/**
 * @name differenceInCalendarYears
 * @category Year Helpers
 * @summary Get the number of calendar years between the given dates.
 *
 * @description
 * Get the number of calendar years between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar years
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar years are between 31 December 2013 and 11 February 2015?
 * const result = differenceInCalendarYears(
 *   new Date(2015, 1, 11),
 *   new Date(2013, 11, 31)
 * )
 * //=> 2
 */

function differenceInCalendarYears(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getFullYear() - dateRight.getFullYear();
}

/**
 * @name differenceInYears
 * @category Year Helpers
 * @summary Get the number of full years between the given dates.
 *
 * @description
 * Get the number of full years between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of full years
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many full years are between 31 December 2013 and 11 February 2015?
 * const result = differenceInYears(new Date(2015, 1, 11), new Date(2013, 11, 31))
 * //=> 1
 */

function differenceInYears(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var sign = compareAsc(dateLeft, dateRight);
  var difference = Math.abs(differenceInCalendarYears(dateLeft, dateRight)); // Set both dates to a valid leap year for accurate comparison when dealing
  // with leap days

  dateLeft.setFullYear(1584);
  dateRight.setFullYear(1584); // Math.abs(diff in full years - diff in calendar years) === 1 if last calendar year is not full
  // If so, result must be decreased by 1 in absolute value

  var isLastYearNotFull = compareAsc(dateLeft, dateRight) === -sign;
  var result = sign * (difference - Number(isLastYearNotFull)); // Prevent negative zero

  return result === 0 ? 0 : result;
}
var OperatorType;
(function (OperatorType) {
  OperatorType["GT"] = "Gt";
  OperatorType["LT"] = "Lt";
})(OperatorType || (OperatorType = {}));
var DateField = function DateField(_ref) {
  var alias = _ref.alias,
    id = _ref.id,
    label = _ref.label,
    description = _ref.description,
    tooltip = _ref.tooltip,
    tooltipLabel = _ref.tooltipLabel,
    mandatoryValidation = _ref.mandatoryValidation,
    dateLimitationValidation = _ref.dateLimitationValidation,
    conditions = _ref.conditions,
    hideDays = _ref.hideDays,
    useLastDayOfMonth = _ref.useLastDayOfMonth;
  var _a;
  var _useFormContext = useFormContext(),
    control = _useFormContext.control,
    errors = _useFormContext.formState.errors,
    watch = _useFormContext.watch;
  var shouldRender = useConditionsWatch(watch, conditions);
  var zeroNumber = 0;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    startDate = _useState2[0],
    setStartDate = _useState2[1];
  var datePickerRef = useRef(null);
  var toUTC = function toUTC(date) {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
  };
  var handleDateChange = function handleDateChange(date, onChange) {
    if (!date) return;
    var newDate = hideDays && useLastDayOfMonth ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : date;
    var utcDate = toUTC(newDate);
    onChange === null || onChange === void 0 ? void 0 : onChange(utcDate);
    setStartDate(newDate);
  };
  return shouldRender ? jsxs("div", Object.assign({
    className: classNames('pp-date-field', {
      'pp-date-field--error': Boolean(errors === null || errors === void 0 ? void 0 : errors[alias]),
      'pp-date-field--hide-days': hideDays
    })
  }, {
    children: [jsx(FieldHead, {
      id: id,
      label: label,
      description: description,
      tooltipText: tooltip,
      tooltipLabel: tooltipLabel
    }), jsx(Controller, {
      name: alias,
      control: control,
      rules: {
        required: mandatoryValidation && mandatoryValidation.errorMessage,
        validate: function validate(value) {
          if (!dateLimitationValidation) return undefined;
          var days = differenceInYears(new Date(), new Date(value));
          if (days < zeroNumber) {
            return dateLimitationValidation.errorMessage;
          }
          if (dateLimitationValidation.operator === OperatorType.GT && days < dateLimitationValidation.value) {
            return dateLimitationValidation.errorMessage;
          }
          if (dateLimitationValidation.operator === OperatorType.LT && days > dateLimitationValidation.value) {
            return dateLimitationValidation.errorMessage;
          }
          return undefined;
        }
      },
      render: function render(_ref2) {
        var _ref2$field = _ref2.field,
          _onChange = _ref2$field.onChange,
          onBlur = _ref2$field.onBlur;
        return jsx(DatePicker, Object.assign({
          className: "react-datepicker__input",
          selected: startDate,
          placeholderText: hideDays ? 'MM/YYYY' : 'DD/MM/YYYY',
          dateFormat: hideDays ? 'MM/yyyy' : 'dd/MM/yyyy',
          showMonthDropdown: true,
          useShortMonthInDropdown: true,
          showYearDropdown: true,
          yearDropdownItemNumber: 6,
          dropdownMode: "scroll",
          onChange: function onChange(date) {
            return handleDateChange(date, _onChange);
          },
          onBlur: onBlur,
          ref: datePickerRef,
          showMonthYearPicker: hideDays
        }, {
          children: jsx("div", Object.assign({
            className: "react-datepicker__close-button-wrapper"
          }, {
            children: jsx("button", {
              className: "react-datepicker__close-button",
              type: "button",
              "aria-label": "close",
              onClick: function onClick() {
                var _a;
                if (datePickerRef.current !== null) {
                  (_a = datePickerRef === null || datePickerRef === void 0 ? void 0 : datePickerRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
                }
              }
            })
          }))
        }));
      }
    }), jsx(ErrorMessage, {
      message: (_a = errors[alias]) === null || _a === void 0 ? void 0 : _a.message
    })]
  })) : null;
};
export { DateField };